import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation } from "../context/LocationContext";

// Debounce utility
const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const SearchBar = () => {
    const [locationQuery, setLocationQuery] = useState("");
    const [locationResults, setLocationResults] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null); // store localityId
    const [searchQuery, setSearchQuery] = useState("");
    const [specializationResults, setSpecializationResults] = useState([]);
    const [doctorResults, setDoctorResults] = useState([]);
    const [hospitalResults, setHospitalResults] = useState([]);
    const { setLocation } = useLocation();

    // 🔗 Redirect helper
    const redirectToProfiles = ({ type, id, name }) => {
        const url = new URL("https://app.gkcklinica.com/public-profiles");
        url.searchParams.set(
            "locality_id",
            selectedLocation?.type === "LOCALITY" ? selectedLocation?.id : "",
        );
        url.searchParams.set(
            "city_id",
            selectedLocation?.type === "CITY" ? selectedLocation?.id : "",
        ); // (if you need, can use selected cityId instead)
        url.searchParams.set(
            "specialization_id",
            type === "specialization" ? id : "",
        );
        url.searchParams.set("search_query", name || "");
        url.searchParams.set("search_type", type);
        window.location.href = url.toString();
    };

    useEffect(() => {
        if (selectedLocation?.type === "CITY") {
            setLocation({
                cityId: selectedLocation?.id || "",
                type: selectedLocation?.type || "",
                name: selectedLocation?.name || "",
            });
        } else {
            setLocation({
                localityId: selectedLocation?.id || "",
                type: selectedLocation?.type || "",
                name: selectedLocation?.name || "",
            });
        }
    }, [selectedLocation]);

    // console.log("Selected Location>>>>", selectedLocation)

    // Fetch locations
    const fetchLocations = async (query) => {
        if (!query) return setLocationResults([]);
        try {
            const res = await axios.get(
                `https://api.gkcklinica.com/user/localitiesCities?limit=5&name=${query}`,
            );
            const merged = [
                ...(res.data?.allCities || []),
                ...(res.data?.allLocalities || []),
            ];
            setLocationResults(merged);
        } catch (error) {
            console.error("Error fetching locations:", error);
        }
    };
    const debouncedLocationSearch = useCallback(
        debounce(fetchLocations, 400),
        [],
    );
    useEffect(() => {
        debouncedLocationSearch(locationQuery);
    }, [locationQuery]);

    // Fetch specializations
    const fetchSpecializations = async (query) => {
        if (!query) return setSpecializationResults([]);
        try {
            const res = await axios.get(
                `https://api.gkcklinica.com/user/specializations?name=${query}`,
            );
            setSpecializationResults(res.data?.data || res.data || []);
        } catch (error) {
            console.error("Error fetching specializations:", error);
        }
    };
    const debouncedSpecializationSearch = useCallback(
        debounce(fetchSpecializations, 400),
        [],
    );

    // Fetch doctors
    const RedirectToDoctors = (query) => {
        if (!selectedLocation?.id) {
            toast.warn("Please select a location first");
            return;
        }
        if (!query) return;

        let localityId = "";
        let cityId = "";

        // Handle conditionally adding localityId or cityId
        if (selectedLocation?.type === "LOCALITY") {
            localityId = selectedLocation?.id;
        } else if (selectedLocation?.type === "CITY") {
            cityId = selectedLocation?.id;
        }

        const url = `https://app.gkcklinica.com/public-profiles?search_query=${query}&search_type=doctors${
            localityId ? `&localityId=${localityId}` : ""
        }${cityId ? `&cityId=${cityId}` : ""}`;

        // Redirect to the external URL
        window.location.href = url;
    };

    // Fetch hospitals
    const RedirectToHospitals = async (query) => {
        if (!selectedLocation?.id) {
            toast.warn("Please select a location first");
            return;
        }
        if (!query) return;

        let localityId = "";
        let cityId = "";

        // Handle conditionally adding localityId or cityId
        if (selectedLocation?.type === "LOCALITY") {
            localityId = selectedLocation?.id;
        } else if (selectedLocation?.type === "CITY") {
            cityId = selectedLocation?.id;
        }

        const url = `https://app.gkcklinica.com/public-profiles?search_query=${query}&search_type=hospitals${
            localityId ? `&localityId=${localityId}` : ""
        }${cityId ? `&cityId=${cityId}` : ""}`;

        // Redirect to the external URL
        window.location.href = url;
    };

    useEffect(() => {
        debouncedSpecializationSearch(searchQuery);
        setDoctorResults([]);
        setHospitalResults([]);
    }, [searchQuery]);

    return (
        <div className="search-wrapper">
            <div className="search-row">
                {/* 🔹 Location Search Box */}
                <div className="search-box">
                    <div className="search-form">
                        <div className="input-wrapper">
                            {/* Location Icon */}
                            <svg
                                className="search-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 1 1 18 0Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <circle
                                    cx="12"
                                    cy="10"
                                    r="3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                            </svg>

                            <input
                                type="text"
                                placeholder="Enter location"
                                value={locationQuery}
                                onChange={(e) =>
                                    setLocationQuery(e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div
                        className={`results ${
                            locationResults.length > 0 ? "active" : ""
                        }`}
                    >
                        <p className="results-title">Recent Search</p>
                        <ul>
                            {locationResults.map((loc, idx) => (
                                <li
                                    key={idx}
                                    onClick={() => {
                                        setLocationQuery(
                                            loc.name ||
                                                loc.city ||
                                                loc.locality,
                                        );
                                        setSelectedLocation({
                                            id: loc.id,
                                            name:
                                                loc.name ||
                                                loc.city ||
                                                loc.locality,
                                            type: loc.districtId
                                                ? "CITY"
                                                : "LOCALITY",
                                        });
                                        setLocationResults([]);
                                    }}
                                >
                                    {/* <i
                                        data-feather="map"
                                        className="result-icon"
                                    ></i> */}
                                    <span className="result-text">
                                        {loc.name || loc.city || loc.locality}
                                    </span>
                                    <span className="result-type">
                                        {loc.districtId
                                            ? "CITY"
                                            : loc?.cities?.name}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* 🔹 Doctor / Hospital / Specialization Search Box */}
                <div className="search-box wide">
                    <div className="search-form">
                        <div className="input-wrapper">
                            {/* Search Icon */}
                            <svg
                                className="search-icon"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    stroke="currentColor"
                                ></path>
                            </svg>

                            <input
                                type="text"
                                placeholder="Search Doctor, Hospital, or Specialization"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />

                            <button
                                type="button"
                                className="search-btn"
                                onClick={() => {
                                    if (!selectedLocation?.id)
                                        toast.warn(
                                            "Please select a location first",
                                        );
                                    else RedirectToDoctors(searchQuery);
                                }}
                            >
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="btn-icon"
                                >
                                    <path
                                        d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        stroke="currentColor"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div
                        className={`results ${
                            doctorResults.length > 0 ||
                            hospitalResults.length > 0 ||
                            specializationResults.length > 0 ||
                            searchQuery
                                ? "active"
                                : ""
                        }`}
                    >
                        <p className="results-title">Suggestions</p>
                        <ul>
                            <li onClick={() => RedirectToDoctors(searchQuery)}>
                                Search by Doctor Name
                            </li>
                            <li
                                onClick={() => RedirectToHospitals(searchQuery)}
                            >
                                Search by Hospital Name
                            </li>

                            {doctorResults.map((doc, idx) => (
                                <li
                                    key={idx}
                                    onClick={() =>
                                        redirectToProfiles({
                                            type: "doctor",
                                            id: doc.id,
                                            name: doc.name,
                                        })
                                    }
                                >
                                    {doc.name}
                                    <span className="result-type">Doctor</span>
                                </li>
                            ))}

                            {hospitalResults.map((hosp, idx) => (
                                <li
                                    key={idx}
                                    onClick={() =>
                                        redirectToProfiles({
                                            type: "hospital",
                                            id: hosp.id,
                                            name: hosp.name,
                                        })
                                    }
                                >
                                    {hosp.name}
                                    <span className="result-type">
                                        Hospital
                                    </span>
                                </li>
                            ))}

                            {specializationResults.map((spec, idx) => (
                                <li
                                    key={idx}
                                    onClick={() =>
                                        redirectToProfiles({
                                            type: "specialization",
                                            id: spec.id,
                                            name: spec.name,
                                        })
                                    }
                                >
                                    {spec.name}
                                    <span className="result-type">
                                        Specialization
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchBar;
